import { DateTime } from "luxon";
import { Ord as StringOrd } from "fp-ts/lib/string";
import { Ord as NumberOrd } from "fp-ts/lib/number";
import { Ord, contramap, fromCompare } from "fp-ts/lib/Ord";
import { pipe } from "fp-ts/lib/function";
import { TCaseDocument6AndCaseDocumentForm1FormList } from "../../models/TFormModels";
import { toDisplayString as CaseDocumentType1ToDisplayString } from "../../../../domain/models/CaseDocumentType1";
import { TCaseDocumentsSort } from "../../state/actions/caseDocumentsSortPersistence";

export type TCaseDocumentForm = TCaseDocument6AndCaseDocumentForm1FormList["forms"][number];

export type TCaseDocumentDateKey = "received_date" | "due_date";

export const OrdByTypeDisplayString: Ord<TCaseDocumentForm> = pipe(
    StringOrd,
    contramap((form: TCaseDocumentForm) => CaseDocumentType1ToDisplayString(form.edit.type)),
);

export const OrdByAdditionalName: Ord<TCaseDocumentForm> = pipe(
    StringOrd,
    contramap((form: TCaseDocumentForm) => form.edit.additional_name),
);

const getDateAsMillis = (form: TCaseDocumentForm, dateKey: TCaseDocumentDateKey): number | null => {
    const value = form.edit[dateKey];
    if (value === null) {
        return null;
    }
    const dateTime = DateTime.fromISO(value);
    return dateTime.isValid ? dateTime.toMillis() : null;
};

const getOrdByDate = (
    dateKey: TCaseDocumentDateKey,
    sort: "newest" | "oldest",
): Ord<TCaseDocumentForm> =>
    fromCompare((first: TCaseDocumentForm, second: TCaseDocumentForm) => {
        const firstMillis = getDateAsMillis(first, dateKey);
        const secondMillis = getDateAsMillis(second, dateKey);

        if (firstMillis === null && secondMillis === null) {
            return 0;
        }
        if (firstMillis === null) {
            return 1;
        }
        if (secondMillis === null) {
            return -1;
        }

        return sort === "newest"
            ? NumberOrd.compare(secondMillis, firstMillis)
            : NumberOrd.compare(firstMillis, secondMillis);
    });

export const getCaseDocumentOrds = (
    sort: TCaseDocumentsSort,
    dateKey: TCaseDocumentDateKey,
): Array<Ord<TCaseDocumentForm>> =>
    sort === "alphabetical"
        ? [OrdByTypeDisplayString, OrdByAdditionalName]
        : [getOrdByDate(dateKey, sort), OrdByTypeDisplayString, OrdByAdditionalName];
